import { HStack, VStack } from '@genial/components';
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Platform } from 'react-native';

import { ReportGeneratorSubCategory } from '../../../../store/reportGenerator/categories';
import { reportGeneratorTheme } from '../../constants/theme';
import { HorizontalCarousel, HorizontalCarouselHandle } from '../HorizontalCarousel';
import { ReportCategoryCard } from '../ReportCategoryCard';

export interface ReportCategoryCardsHandle {
  scrollToPage: (page: number) => void;
}

interface ReportCategoryCardsProps {
  subCategories: ReportGeneratorSubCategory[];
  onGenerateReport: (subCategory: ReportGeneratorSubCategory) => void;
  onActivePageChange?: (page: number, pageCount: number) => void;
}

const CarouselItem: React.FC<{ isLast: boolean; children: React.ReactNode }> = ({
  isLast,
  children,
}) => {
  const gap = isLast ? 0 : reportGeneratorTheme.carousel.gap;

  if (Platform.OS !== 'web') {
    return <VStack style={{ paddingRight: gap }}>{children}</VStack>;
  }

  return (
    <div
      style={{
        scrollSnapAlign: 'start',
        flexShrink: 0,
        marginRight: gap,
      }}>
      {children}
    </div>
  );
};

const ReportCategoryCardsList: React.FC<{
  subCategories: ReportGeneratorSubCategory[];
  cardWidth: number;
  onGenerateReport: (subCategory: ReportGeneratorSubCategory) => void;
}> = ({ subCategories, cardWidth, onGenerateReport }) => {
  const items = subCategories.map((subCategory, index) => (
    <CarouselItem key={subCategory.id} isLast={index === subCategories.length - 1}>
      <ReportCategoryCard
        subCategory={subCategory}
        width={cardWidth}
        onGenerateReport={onGenerateReport}
      />
    </CarouselItem>
  ));

  if (Platform.OS !== 'web') {
    return <HStack>{items}</HStack>;
  }

  return <>{items}</>;
};

export const ReportCategoryCards = forwardRef<ReportCategoryCardsHandle, ReportCategoryCardsProps>(
  ({ subCategories, onGenerateReport, onActivePageChange }, ref) => {
    const carouselRef = useRef<HorizontalCarouselHandle>(null);
    const onActivePageChangeRef = useRef(onActivePageChange);
    onActivePageChangeRef.current = onActivePageChange;

    const cardWidth = 320;
    const scrollStep = cardWidth + reportGeneratorTheme.carousel.gap;

    useImperativeHandle(
      ref,
      () => ({
        scrollToPage: (page: number) => {
          carouselRef.current?.scrollToPage(page);
        },
      }),
      []
    );

    useEffect(() => {
      carouselRef.current?.scrollToPage(0);
    }, [subCategories]);

    if (!subCategories.length) {
      return null;
    }

    return (
      <VStack width="full" flexShrink={0}>
        <HorizontalCarousel
          ref={carouselRef}
          itemCount={subCategories.length}
          scrollStep={scrollStep}
          onActivePageChange={(page, totalPages) => {
            onActivePageChangeRef.current?.(page, totalPages);
          }}
          contentContainerStyle={{}}>
          <ReportCategoryCardsList
            subCategories={subCategories}
            cardWidth={cardWidth}
            onGenerateReport={onGenerateReport}
          />
        </HorizontalCarousel>
      </VStack>
    );
  }
);

ReportCategoryCards.displayName = 'ReportCategoryCards';
